import type { NearbyOfficeOption } from "@homeinstead/marketing-api";
import type { FieldValues, Path, PathValue } from "react-hook-form";

import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./Select";

type StringFieldName<T extends FieldValues> = {
  [K in Path<T>]: PathValue<T, K> extends string ? K : never;
}[Path<T>];

type NumberFieldName<T extends FieldValues> = {
  [K in Path<T>]: PathValue<T, K> extends number ? K : never;
}[Path<T>];

interface OfficeSelectProps<T extends FieldValues> {
  officeOptions: NearbyOfficeOption[];
  /**
   * Which field in your form is the final postal code stored into?
   * Must point to a `string` field in your Zod/React Hook Form schema.
   */
  postalCodeFieldName: StringFieldName<T>;

  /**
   * Which field in your form is the office ID stored into?
   * Must point to a `number` field in your Zod/React Hook Form schema.
   */
  officeIdFieldName: NumberFieldName<T>;

  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function findOfficeOptionForSelection<T extends FieldValues>(
  officeOptions: NearbyOfficeOption[],
  officeId: PathValue<T, NumberFieldName<T>> | undefined,
  postalCode: PathValue<T, StringFieldName<T>> | undefined,
): NearbyOfficeOption | undefined {
  if (officeId) {
    const matchingPostal = officeOptions.find(
      (office) =>
        office.officeID === officeId &&
        office.address.postalCode === postalCode,
    );
    if (matchingPostal) {
      return matchingPostal;
    }

    return officeOptions.find((office) => office.officeID === officeId);
  }

  return undefined;
}

function OfficeSelect<T extends FieldValues>({
  officeOptions,
  postalCodeFieldName,
  officeIdFieldName,
  open,
  onOpenChange,
}: OfficeSelectProps<T>) {
  const { control, setValue } = useFormContext<T>();

  const visibleOffices = officeOptions.filter((office) =>
    Boolean(office.locationName?.trim()),
  );

  const officeId = useWatch({ name: officeIdFieldName });
  const postalCode = useWatch({ name: postalCodeFieldName });
  const firstOffice = visibleOffices[0];
  const selectedOffice = findOfficeOptionForSelection(
    visibleOffices,
    officeId,
    postalCode,
  );

  React.useEffect(() => {
    if (!officeId && firstOffice) {
      setValue(
        officeIdFieldName,
        firstOffice.officeID as PathValue<T, typeof officeIdFieldName>,
        {
          shouldDirty: false,
        },
      );
      setValue(
        postalCodeFieldName,
        firstOffice.address.postalCode as PathValue<
          T,
          typeof postalCodeFieldName
        >,
        {
          shouldDirty: false,
        },
      );
    }
  }, [officeId, firstOffice, setValue, officeIdFieldName, postalCodeFieldName]);

  const handleOfficeChange = (officeIdValue: string) => {
    const matchedOffice = officeOptions.find(
      (office) => office.officeID === Number(officeIdValue),
    );

    if (!matchedOffice?.address.postalCode) {
      return;
    }

    setValue(
      postalCodeFieldName,
      matchedOffice.address.postalCode as PathValue<
        T,
        typeof postalCodeFieldName
      >,
      { shouldDirty: true, shouldValidate: true },
    );

    setValue(
      officeIdFieldName,
      matchedOffice.officeID as PathValue<T, typeof officeIdFieldName>,
      { shouldDirty: true, shouldValidate: true },
    );
  };

  return (
    <Controller
      control={control}
      name={officeIdFieldName}
      render={({ field }) => {
        const selectedOfficeId = String(
          selectedOffice?.officeID ?? firstOffice?.officeID ?? "",
        );

        return (
          <Select
            name={officeIdFieldName}
            onOpenChange={onOpenChange}
            onValueChange={(value: string) => {
              const matchedOffice = officeOptions.find(
                (office) => office.officeID === Number(value),
              );
              if (matchedOffice) {
                field.onChange(matchedOffice.officeID);
              }
              handleOfficeChange(value);
            }}
            open={open}
            value={selectedOfficeId}
          >
            <SelectTrigger label="Office">
              <SelectValue
                placeholder={
                  selectedOffice?.locationName ||
                  firstOffice?.locationName ||
                  "Select office"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {visibleOffices.map((office) => (
                <SelectItem
                  key={office.optionId}
                  value={String(office.officeID)}
                >
                  {office.locationName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }}
    />
  );
}

export { OfficeSelect };
